import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { UserTwoIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import ProfileViewContainer from '@repo/ui/profile-view-container'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'
import { endOfDay, isWithinInterval, startOfDay, startOfToday } from 'date-fns'
import { useMemo } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { useFormContext } from 'react-hook-form'

import { Paths } from '../../../lib/constants/path'
import useTranslate from '../../../lib/hooks/use-translate'
import { staffPositionEnumMapping } from '../../../lib/types/enums/staff/staff-position.enum'
import { staffQualificationEnumMapping } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaffInput } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { StaffProperties } from '../../../lib/types/models/staff/properties.staff'
import { Staff } from '../../../lib/types/models/staff/staff'
import { StaffRequest } from '../../../lib/types/models/staff-request/staff-request'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import { constructCenterPath } from '../../../views/centers/utils'

type Props = {
  staff: Staff
  staffRequest: StaffRequest
}

const AffectedStaffViewHeader = ({ staff, staffRequest }: Props) => {
  const { navigate } = useNavigator()
  const form = useFormContext<AffectedStaffInput>()
  const { t } = useTranslate()

  const startDate = form.watch('startDate')
  const endDate = form.watch('endDate')

  const isStaffOnTrip = useMemo(() => {
    if (!startDate || !endDate) return false
    if (staffRequest.centers.length === 0) return false

    const today = startOfToday()
    const start = startOfDay(startDate)
    const end = endOfDay(endDate)

    return isWithinInterval(today, { start, end })
  }, [startDate, endDate, staffRequest.centers.length])

  return (
    <InnerBox className="p-4 rounded-lg">
      <div className="flex justify-between items-center w-full">
        <div className="flex  gap-3 items-center">
          <ProfileViewContainer
            path={staff.picture?.path}
            onClick={() => navigate(Paths.STAFF_UPDATE_PAGE.replace(':id', staff.id.toString()))}
          />
          <div className="flex flex-col gap-2">
            <div className="flex gap-3 items-center">
              <Typography.Link
                variant="text"
                onClick={() =>
                  navigate(Paths.STAFF_UPDATE_PAGE.replace(':id', staff.id.toString()))
                }
              >
                {toFullName(staff.firstName, staff.lastName, {
                  options: { mode: 'lastname-firstname' }
                })}
              </Typography.Link>
              {isStaffOnTrip && (
                <Badge
                  text={
                    <div className="flex gap-1 items-center">
                      <Typography variant="label-semibold" color={tailwindColors.fushia.DEFAULT}>
                        En séjour actuellement -
                      </Typography>

                      {staffRequest.centers.map((center, index) => (
                        <Fragment key={center.id}>
                          <TextLink
                            name={center.name}
                            href={constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, center.id)}
                          />

                          {index < staffRequest.centers.length - 1 && ', '}
                        </Fragment>
                      ))}
                    </div>
                  }
                />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
                {`${staff.address1}, ${staff.postalCode} ${staff.city}, ${staff.country}`}
              </Typography>
              <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
                {`Ancienneté : ${StaffProperties.getSeniority(staff)}`}
              </Typography>
              <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
                {`${formatPhoneNumber(staff.phoneNumber)} - `}
                <TextLink href={`mailto:${staff.user.email}`} name={staff.user.email} />
              </Typography>
              <div className="flex gap-1">
                <Badge
                  color={tailwindColors.white.hover}
                  borderColor={tailwindColors.grey.light.DEFAULT}
                  text={
                    <div className="flex gap-1 items-center">
                      <Typography variant="label" color={tailwindColors.black.DEFAULT}>
                        Poste :
                      </Typography>
                      <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                        {`${t(staffPositionEnumMapping[staff.position])}`}
                      </Typography>
                    </div>
                  }
                />
                -
                <Badge
                  color={tailwindColors.white.hover}
                  borderColor={tailwindColors.grey.light.DEFAULT}
                  text={
                    <div className="flex gap-1 items-center">
                      <Typography variant="label" color={tailwindColors.black.DEFAULT}>
                        Qualification :
                      </Typography>
                      <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                        {t(staffQualificationEnumMapping[staffRequest.qualification].label)}
                      </Typography>
                    </div>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <Badge.Link
          icon={<UserTwoIcon color={tailwindColors.primary.DEFAULT} />}
          iconPosition="start"
          variant="white"
          text="Voir le profil"
          size="medium"
          onClick={() => navigate(Paths.STAFF_UPDATE_PAGE.replace(':id', staff.id.toString()))}
          textColor={tailwindColors.primary.DEFAULT}
          style={{ textDecorationColor: tailwindColors.primary.DEFAULT }}
        />
      </div>
    </InnerBox>
  )
}

export default AffectedStaffViewHeader
